import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, cn } from "@nexus/ui";
import { toast } from "sonner";
import {
  scriptReviewRejectionKeys,
  scriptReviewRejectionsQueryOptions,
} from "@/lib/queries/script-review-rejections.queries";
import { promptsKeys } from "@/lib/queries/prompts.queries";
import {
  actionScriptReviewRejection,
  type ScriptReviewRejection,
} from "@/features/script-review/api/script-review-rejections.api";
import { trackScriptReviewSuggestionActioned } from "@/lib/analytics-events";
import { translateScriptReviewCommentType } from "@/features/script-review/lib/script-review-status";
import { diffPromptWords } from "@/features/script-review/lib/prompt-diff";

// Per comment-type chip colors — mirrors the player's category identity.
const TYPE_CHIP: Record<string, string> = {
  VOICEOVER: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  TEXT_OVERLAY: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  VISUAL: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
};

function StatusChip({ status }: { status: ScriptReviewRejection["status"] }) {
  const { t } = useTranslation();
  const map: Record<string, { label: string; cls: string }> = {
    PENDING: {
      label: t("scriptReview.rejections.statusPending", "Generating…"),
      cls: "border-border bg-muted text-muted-foreground",
    },
    READY: {
      label: t("scriptReview.rejections.statusReady", "Suggestion ready"),
      cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
    },
    NOT_APPLICABLE: {
      label: t("scriptReview.rejections.statusNotApplicable", "No change advised"),
      cls: "border-border bg-muted text-muted-foreground",
    },
    FAILED: {
      label: t("scriptReview.rejections.statusFailed", "Failed"),
      cls: "border-destructive/40 bg-destructive/10 text-destructive",
    },
  };
  const chip = map[status] ?? map.PENDING;
  return (
    <span className={cn("shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium", chip.cls)}>
      {chip.label}
    </span>
  );
}

/**
 * Prompt-improvement tickets: AI comments an admin rejected WITH a reason, each
 * with a worker-drafted prompt edit. Approve publishes the edit as a new global
 * prompt version; Ignore dismisses. Rendered on the hub for Adge admins ONLY
 * (gated at the call site) — the suggestions modify GLOBAL prompts.
 */
export function ScriptReviewRejectionsSection() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(scriptReviewRejectionsQueryOptions());
  const items = data?.items ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const actionMut = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "ignore" }) =>
      actionScriptReviewRejection(id, action),
    onSuccess: (_res, vars) => {
      const ticket = items.find((i) => i.id === vars.id);
      trackScriptReviewSuggestionActioned({
        rejection_id: vars.id,
        action: vars.action === "approve" ? "approved" : "ignored",
        prompt_key: ticket?.promptKey ?? "",
      });
      void qc.invalidateQueries({ queryKey: scriptReviewRejectionKeys.all });
      if (vars.action === "approve") {
        // A new prompt version exists — refresh the prompts section + manager.
        void qc.invalidateQueries({ queryKey: promptsKeys.all });
        toast.success(
          t("scriptReview.rejections.approved", "Prompt updated — new version created"),
        );
      } else {
        toast.success(t("scriptReview.rejections.ignored", "Ticket dismissed"));
      }
    },
    // Surface the server's precise message (409 stale guard etc.).
    onError: (e) =>
      toast.error(
        e instanceof Error ? e.message : t("scriptReview.rejections.error", "Could not update it"),
      ),
  });

  if (!isLoading && items.length === 0) {
    return (
      <section className="flex flex-col gap-3">
        <SectionHeading />
        <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
          {t(
            "scriptReview.rejections.empty",
            "No open tickets. Rejecting an AI comment with a reason creates one here.",
          )}
        </p>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading />
      <div className="overflow-hidden rounded-lg border border-border">
        {isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">
            {t("scriptReview.rejections.loading", "Loading…")}
          </p>
        ) : (
          items.map((ticket) => {
            const expanded = expandedId === ticket.id;
            const busy = actionMut.isPending && actionMut.variables?.id === ticket.id;
            return (
              <div
                key={ticket.id}
                className="flex flex-col gap-2 border-b border-border px-4 py-3 last:border-0"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      <span
                        className={cn(
                          "rounded border px-1.5 py-0.5 font-medium",
                          TYPE_CHIP[ticket.commentType] ?? "border-border bg-muted",
                        )}
                      >
                        {translateScriptReviewCommentType(t, ticket.commentType)}
                      </span>
                      <StatusChip status={ticket.status} />
                      <span className="text-muted-foreground">
                        {new Date(ticket.rejectedAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="truncate text-sm" title={ticket.commentText}>
                      {ticket.commentText}
                    </p>
                    <p className="text-xs text-muted-foreground" title={ticket.reason}>
                      <span className="font-medium">
                        {t("scriptReview.rejections.reasonLabel", "Reason:")}
                      </span>{" "}
                      {ticket.reason}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {ticket.status === "READY" && (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => actionMut.mutate({ id: ticket.id, action: "approve" })}
                      >
                        {busy
                          ? t("scriptReview.rejections.applying", "Applying…")
                          : t("scriptReview.rejections.approve", "Approve")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => actionMut.mutate({ id: ticket.id, action: "ignore" })}
                    >
                      {t("scriptReview.rejections.ignore", "Ignore")}
                    </Button>
                  </div>
                </div>

                {/* AI recommendation — rationale + expandable proposed prompt. */}
                {ticket.status === "READY" && ticket.suggestionRationale && (
                  <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 p-2 text-xs">
                    <p>
                      <span className="font-medium">
                        {t("scriptReview.rejections.recommendation", "AI recommendation:")}
                      </span>{" "}
                      {ticket.suggestionRationale}
                    </p>
                    <button
                      type="button"
                      className="self-start text-primary underline"
                      onClick={() => setExpandedId(expanded ? null : ticket.id)}
                    >
                      {expanded
                        ? t("scriptReview.rejections.hidePrompt", "Hide proposed prompt")
                        : t("scriptReview.rejections.showPrompt", "Show the exact edit")}
                    </button>
                    {expanded && ticket.suggestedPrompt && (
                      <PromptDiff
                        before={ticket.basePromptContent}
                        after={ticket.suggestedPrompt}
                      />
                    )}
                  </div>
                )}
                {ticket.status === "NOT_APPLICABLE" && ticket.suggestionRationale && (
                  <p className="text-xs text-muted-foreground">{ticket.suggestionRationale}</p>
                )}
                {ticket.status === "FAILED" && ticket.suggestionError && (
                  <p className="text-xs text-destructive">{ticket.suggestionError}</p>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

const PROMPT_BOX = "max-h-64 overflow-y-auto whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px]";

/**
 * The proposed prompt rendered as a diff against the version the suggestion was
 * drafted from, so the admin sees WHERE the edit lands instead of re-reading the
 * whole prompt to find it. `<mark>`/`<del>` carry the meaning for screen readers
 * (colour alone would not). Falls back to the raw proposal when the base version
 * can't be resolved — better a plain prompt than no prompt.
 */
function PromptDiff({ before, after }: { before: string | null; after: string }) {
  const { t } = useTranslation();
  const segments = useMemo(
    () => (before === null ? null : diffPromptWords(before, after)),
    [before, after],
  );

  if (!segments) {
    return <pre className={PROMPT_BOX}>{after}</pre>;
  }

  const changed = segments.some((s) => s.op !== "same");
  return (
    <div className="flex flex-col gap-1">
      <p className="text-[11px] text-muted-foreground">
        {changed
          ? t(
              "scriptReview.rejections.diffLegend",
              "Compared to the prompt version the AI read when drafting this suggestion: added, removed.",
            )
          : t(
              "scriptReview.rejections.diffIdentical",
              "This suggestion is identical to that prompt version.",
            )}
      </p>
      <pre className={PROMPT_BOX}>
        {segments.map((segment, i) => {
          if (segment.op === "same") return <span key={i}>{segment.text}</span>;
          if (segment.op === "added") {
            return (
              <mark
                key={i}
                className="rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
              >
                {segment.text}
              </mark>
            );
          }
          return (
            <del key={i} className="rounded bg-destructive/10 text-destructive">
              {segment.text}
            </del>
          );
        })}
      </pre>
    </div>
  );
}

function SectionHeading() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-0.5">
      <h2 className="text-sm font-medium text-muted-foreground">
        {t("scriptReview.rejections.heading", "Prompt improvement")}
      </h2>
      <p className="text-xs text-muted-foreground">
        {t(
          "scriptReview.rejections.hint",
          "AI comments rejected with a reason land here with an AI-drafted prompt fix. Approving publishes it as a new version of the global prompt.",
        )}
      </p>
    </div>
  );
}
